import { AnimationSystemEvent } from './AnimationSystem';
import AnimationClip, { AnimationClipEvent } from './AnimationClip';
import * as Animations from './Clip';
import GameObject from '../../GameObject/GameObject';
import Game from '../../Game';
import Scene from '../../Scene/Scene';
import WorldSystem from '../../System/World/WorldSystem';
import CardSystem from '../../System/Card/CardSystem';
import { GameScene } from '../../State/Game/GameScene';
import { report } from '../../report';
import { IMainAnimMetaData, MainAnim } from './Clip/MainAnim';
import TransformComponent from '../../Component/Transform';
import Renderable from '../../Component/Renderable';
import System from 'System/System';
import Animator from 'Prototype/Animator';

export function GetAnimationClip(
    clipName: string,
    gameObject: GameObject,
    meta?: any
): AnimationClip {
    if (!(<any>Animations)[clipName]) {
        console.log('<any>Animations', <any>Animations);
        throw new Error(`Animation clip '${clipName}' not found.`);
    }

    const animationClip: AnimationClip = new (<any>Animations)[clipName](
        gameObject,
        meta
    );

    return animationClip;
}

export function* PlayAnimation(clip: AnimationClip): IterableIterator<number> {
    if (!clip.gameObject) throw new Error(`GameObject is undefined.`);

    let isPlaying = true;

    clip.on(AnimationClipEvent.DESTROY, () => {
        isPlaying = false;
    });
    clip.on(AnimationClipEvent.COMPLETE, () => {
        isPlaying = false;
    });

    if (clip.gameObject.scene !== null) {
        clip.gameObject.scene.emit(AnimationSystemEvent.PLAY, clip);
    } else {
        console.warn(
            `Gameobject '${clip.gameObject}' could not play its animation.`
        );
        clip.emit(AnimationClipEvent.COMPLETE);
    }

    while (isPlaying) yield;
}

/** @deprecated Use StartAnimationClipAsync instead */
export function StartAnimationAsync(
    gameObject: GameObject,
    clipName: string,
    meta?: any
) {
    const animationClip = GetAnimationClip(clipName, gameObject, meta);

    return new Promise(resolve => {
        animationClip.once(AnimationClipEvent.COMPLETE, resolve);
        gameObject.scene.emit(AnimationSystemEvent.PLAY, animationClip);
    });
}

/** @param gameScene aka state.activeScene.result */
type AnimatorSystem = System & {
    animator: Animator
}
export function getCurrentAnimatorAndScene(gameScene: GameScene) {
    const scene: Scene = Game.scene.getActiveScene();
    let system: AnimatorSystem;

    switch (gameScene) {
        case GameScene.WORLD:
            system = scene.getSystem(WorldSystem);
            break;
        case GameScene.LEVEL:
            system = scene.getSystem(CardSystem);
            break;
    }

    const animator = system?.animator;

    if (!animator) {
        report('Game scene does not have animator', {
            once: true,
            tags: { gameScene, systemFound: Boolean(system) },
        });
    }

    return {
        animator,
        scene,
    };
}

/** @deprecated Use StartAnimationClip instead */
export function StartAnimation(
    gameObject: GameObject,
    clipName: string,
    meta?: any
): AnimationClip {
    try {
        if (!gameObject) throw new Error(`GameObject is undefined.`);

        const animationClip = GetAnimationClip(clipName, gameObject, meta);

        const scene = Game.scene.getActiveScene();
        scene?.emit(AnimationSystemEvent.PLAY, animationClip);

        return animationClip;
    } catch (error) {
        report('Failed to start animation', {
            error,
            once: true,
            sampleRate: 0.1,
            tags: {
                clipName,
            },
        });
    }
}

export function StopAnimation(gameObject: GameObject) {
    try {
        const scene = Game.scene.getActiveScene();
        scene?.emit(AnimationSystemEvent.STOP, gameObject);
    } catch (error) {
        report('Failed to stop animation', {
            error,
            once: true,
            sampleRate: 0.1,
        });
    }
}

export function StartAnimationClip(clip: AnimationClip): void {
    try {
        const scene = Game.scene.getActiveScene();
        scene?.emit(AnimationSystemEvent.PLAY, clip);
    } catch (error) {
        report('Failed to start animation clip', {
            error,
            once: true,
            sampleRate: 0.1,
        });
    }
}
export function StartAnimationClipAsync(
    clip: AnimationClip
): Promise<void> {
    return new Promise(resolve => {
        clip.once(AnimationClipEvent.DESTROY, resolve);
        const scene = Game.scene.getActiveScene();
        scene?.emit(AnimationSystemEvent.PLAY, clip);
    });
}

export function Anim(
    gameObject: GameObject,
    meta: IMainAnimMetaData,
    additive: boolean = false,
): Promise<void> {
    return StartAnimationClipAsync(new MainAnim(gameObject, meta, additive));
}

type Descriptor = { get: (o: GameObject) => number; set: (o: GameObject, v: number) => void };

const animPropsDescriptors: Record<string, Descriptor> = {
    x: {
        get: (o) => o.getComponent(TransformComponent).x,
        set: (o, v) => o.getComponent(TransformComponent).x = v,
    },
    y: {
        get: (o) => o.getComponent(TransformComponent).y,
        set: (o, v) => o.getComponent(TransformComponent).y = v,
    },
    alpha: {
        get: (o) => o.getComponent(Renderable).alpha,
        set: (o, v) => o.getComponent(Renderable).alpha = v,
    },
    angle: {
        get: (o) => o.getComponent(TransformComponent).rotation * 180 / Math.PI,
        set: (o, v) => o.getComponent(TransformComponent).rotation = v / 180 * Math.PI,
    },
    rotation: {
        get: (o) => o.getComponent(TransformComponent).rotation,
        set: (o, v) => o.getComponent(TransformComponent).rotation = v,
    },
    scaleX: {
        get: (o) => o.getComponent(TransformComponent).scale.x,
        set: (o, v) => o.getComponent(TransformComponent).scale.x = v,
    },
    scaleY: {
        get: (o) => o.getComponent(TransformComponent).scale.y,
        set: (o, v) => o.getComponent(TransformComponent).scale.y = v,
    },
};

export function AnimFrom(
    gameObject: GameObject,
    meta: IMainAnimMetaData,
): Promise<void> {
    const to: any = {};

    Object.entries(meta).forEach(([key, value]) => {
        const descriptor = animPropsDescriptors[key];

        if (descriptor) {
            if (key === 'alpha') {
                gameObject.addComponent(Renderable);
            } else {
                gameObject.addComponent(TransformComponent);
            }

            to[key] = descriptor.get(gameObject);
            descriptor.set(gameObject, value);
        }
    });

    return StartAnimationClipAsync(new MainAnim(gameObject, { ...meta, ...to }));
}

export function* AwaitIterators(
    iterators: IterableIterator<number>[]
): IterableIterator<number> {
    while (iterators.length > 0) {
        let d = yield;
        const newIterators = [];
        for (let iterator of iterators) {
            if (!iterator.next(d).done) newIterators.push(iterator);
        }
        iterators = newIterators;
    }
}

export function* ConcatIterators(
    iterators: IterableIterator<number>[]
): IterableIterator<number> {
    for (let iterator of iterators) {
        yield* iterator;
    }
}
